import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { select_is_authenticated, select_user_role } from '../../store/auth_slice';
import type { User } from '../../types/common_types';

interface ProtectedRouteProps {
  children: ReactNode;
  required_role?: User['role'];
  required_roles?: User['role'][];
}

export const ProtectedRoute = ({ children, required_role, required_roles }: ProtectedRouteProps) => {
  const location = useLocation();
  const is_authenticated = useSelector(select_is_authenticated);
  const user_role = useSelector(select_user_role);

  // Not authenticated - redirect to login with return path
  if (!is_authenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if required
  if (required_role || required_roles) {
    const role_hierarchy: Record<User['role'], number> = {
      'admin': 3,
      'operator': 2,
      'manager': 2,
      viewer: 0
    };

    const user_level = user_role ? role_hierarchy[user_role] : 0;

    // Check single required role
    if (required_role) {
      const required_level = role_hierarchy[required_role];
      if (user_level < required_level) {
        return <Navigate to="/unauthorized" replace />;
      }
    }

    // Check multiple allowed roles
    if (required_roles && user_role) {
      if (!required_roles.includes(user_role)) {
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  return <>{children}</>;
};